import ProductStore from '../stores/product-store'
import ProductCalculations from '../utils/product-calculations'
import Config from '../config'
import ImageStore from '../stores/image-store'

/**
 * This class will have objects containing a reference to an image and a product.
 * It will contain data on what the customer has selected, which product and image and options combined
 */
function OpeOrderItem(imageId, productId, options, isPortrait) {
    if (!imageId) {
        throw new OpeException("Cannot create a new instance of OpeOrderItem w/o imageId parameter");
    }
    this.imageId = imageId;
    this.productId = productId;
    this.imageTakenDate = null;
    this.options = options;
    this.options.upsale = {};
    this.information = {
        lowRes: false,
        autoCrop: false,
        lowResOverride: null,
        autoCropOverride: null,
    }; // Object for warning about low res and auto crop
    this.isPortrait = !!isPortrait;
}

/**
 * This will give you an id to use for this OrderItem.
 * The data within this object does not contain any unique
 * identification for an order item, therefore we create
 * an id using a long string of random letters and numbers.
 * The id is created only once of every object when this method is called
 */
OpeOrderItem.prototype.getId = function () {
    if (!this.id) {
        //Generate an id for this item
        this.id = this._makeId(16);
    }
    return this.id;
};

/**
 * This will get you an id for an OpeImage object
 * @return {string}
 */
OpeOrderItem.prototype.getImageId = function () {
    return this.imageId;
};

/**
 * This will get you the productId. Note that productId could be null while
 * waiting for some async request to get the data we need for the product
 * @return {null|string}
 */
OpeOrderItem.prototype.getProductId = function () {
    return this.productId;
};

/**
 * Get the name of the product to display to the customer
 * @return {string}
 */
OpeOrderItem.prototype.getProductLabel = function () {
    if (this.getProductId()) {
        //Get product
        var product = ProductStore.getProductById(this.getProductId());

        return product.getWidthOptionTitle(this.getProductWidth());
    }
    return null;
};

/**
 * Check if this item has enabled the given upsale option
 * @param  {string}
 * @return {Boolean}
 */
OpeOrderItem.prototype.hasOptionByName = function (name) {
    if (this.options.upsale[name]) {
        return true;
    }
    return false;
};

/**
 * Enable or disable an upsale option by name
 * @param {string}
 * @param {boolean}
 */
OpeOrderItem.prototype.setOptionByName = function (name, enabled) {
    this.options.upsale[name] = enabled;

    //Special handling if this option change was a paper type change
    this._handlePaperTypeChange(name, enabled);

};

/**
 * Set whether or not this item should have an auto crop warning
 * @param {boolean} warning
 */
OpeOrderItem.prototype.setAutoCropWarning = function (warning) {
    this.information.autoCrop = warning;
};

/**
 * Set whether or not this item should have an low res warning
 * @param {boolean} warning
 */
OpeOrderItem.prototype.setLowResWarning = function (warning) {
    this.information.lowRes = warning;
};

/**
 * This allows one to temporary override the auto crop setting, while allowing the auto crop setting
 * to still be changed while the temporary override is in place
 *
 * Set to null to remove the override
 *
 * @param {boolean} warning
 */
OpeOrderItem.prototype.setAutoCropWarningOverride = function (warning) {
    this.information.autoCropOverride = warning;
};

/**
 * This allows one to temporary override the low res setting, while allowing the low res setting
 * to still be changed while the temporary override is in place
 *
 * Set to null to remove the override
 *
 * @param {boolean} warning
 */
OpeOrderItem.prototype.setLowResWarningOverride = function (warning) {
    this.information.lowResOverride = warning;
};

/**
 * Has this item currently got an auto crop warning?
 */
OpeOrderItem.prototype.getAutoCropWarning = function () {
    if (this.information.autoCropOverride !== null) {
        return this.information.autoCropOverride;
    }
    return this.information.autoCrop;
};

/**
 * Has this item currently got an low res warning?
 */
OpeOrderItem.prototype.getLowResWarning = function () {
    if (this.information.lowResOverride !== null) {
        return this.information.lowResOverride;
    }
    return this.information.lowRes;
};

OpeOrderItem.prototype.setImageTakenDate = function (date) {

    if (!date) {
        return;
    }

    if (Number.isInteger(date)) {
        this.imageTakenDate = date;
    } else {
        var d = Date.parse(date);

        if (!isNaN(d)) {
            this.imageTakenDate = d;
        }
    }
};

OpeOrderItem.prototype.getImageTakenDate = function () {
    return this.imageTakenDate;
};

/**
 * Paper type, usually matte and glossy, needs special handling when swithing between the different types.
 * There is a default type for each vendor, and this default needs to be disabled if another paper type is enabled.
 * It also needs to be enabled again if another paper type is disabled. So if default is matte, remove matte when glossy is enabled,
 * and enable it again if the glossy is deselected.
 */
OpeOrderItem.prototype._handlePaperTypeChange = function (name, enabled) {
    var product = ProductStore.getProductById(this.getProductId());

    var upsaleOptions = product.getUpsaleOptions();

    //Only need to do this if the option changed is a paper type option
    if (upsaleOptions[name].type === "paperType") {

        //Disable all paper types exept the selected one
        for (var option in upsaleOptions) {
            if (upsaleOptions[option].type === "paperType" && option != name) {
                this.options.upsale[option] = false;
            }
        }

        //How many enabled paper types are there?
        var enabledPaperTypes = 0;
        for (var option in upsaleOptions) {
            if (upsaleOptions[option].type === "paperType") {
                if (this.options.upsale[option]) {
                    enabledPaperTypes++;
                }
            }
        }

        //If there are no enabled paper types, we should enable the default one
        if (enabledPaperTypes === 0) {
            for (var option in upsaleOptions) {
                if (upsaleOptions[option].type === "paperType" && Config.VendorDefaults.paperType().toLowerCase() === option) {
                    this.options.upsale[option] = true;
                }
            }
        }
    }
};


OpeOrderItem.prototype.getOptions = function () {
    return this.options.upsale;
};

OpeOrderItem.prototype.getImagePositionData = function () {
    return this.options.position;
};

OpeOrderItem.prototype.getImageScale = function () {
    return this.options.scale;
};

/**
 * Get the price multiplied by quantity of this item
 * Choose if you want to exclude the prices for upsale options or not
 * @param  {int}
 * @param  {boolean}
 * @return {float|null}
 */
OpeOrderItem.prototype.getBasePrice = function (decimals, excludingUpsaleOptions) {
    if (this.getProductId()) {
        if (!decimals) {
            decimals = 2;
        }

        var decimalMultiplier = Math.pow(10, decimals);

        //Get product
        var product = ProductStore.getProductById(this.getProductId());

        //Get width option price
        var price = (product.getWidthOptionBasePrice(this.getProductWidth()) * this.getQuantity());

        if (!excludingUpsaleOptions) {
            //Get upsale options price
            for (var option in this.options.upsale) {
                if (this.options.upsale.hasOwnProperty(option)) {
                    if (this.options.upsale[option]) { //Option enabled
                        price += (product.getOptionBasePriceByName(option) * this.getQuantity());
                    }
                }
            }
        }


        //Round the number to include the number of decimals given as argument to this function
        return Math.round(price * decimalMultiplier) / decimalMultiplier;
    }

    return null;
};

/**
 * Get the price multiplied by quantity of this item
 * Choose if you want to exclude the prices for upsale options or not
 * @param  {int}
 * @param  {boolean}
 * @return {float}
 */
OpeOrderItem.prototype.getPrice = function (decimals, excludingUpsaleOptions) {
    if (this.getProductId()) {
        if (!decimals) {
            decimals = 2;
        }

        var decimalMultiplier = Math.pow(10, decimals);

        //Get product
        var product = ProductStore.getProductById(this.getProductId());

        //Get width option price
        var price = (product.getWidthOptionPrice(this.getProductWidth()) * this.getQuantity());

        if (!excludingUpsaleOptions) {
            //Get upsale options price
            for (var option in this.options.upsale) {
                if (this.options.upsale.hasOwnProperty(option)) {
                    if (this.options.upsale[option]) { //Option enabled
                        price += (product.getOptionPriceByName(option) * this.getQuantity());
                    }
                }
            }
        }


        //Round the number to include the number of decimals given as argument to this function
        return Math.round(price * decimalMultiplier) / decimalMultiplier;
    }

    return null;
};


/**
 * If any upsale options have been selected for this product, remove them
 */
OpeOrderItem.prototype.resetUpsaleOptions = function () {
    for (var option in this.options.upsale) {
        if (this.options.upsale.hasOwnProperty(option)) {
            this.options.upsale[option] = false;
        }
    }

    //Set default options
    try {
        this.options.upsale[Config.VendorDefaults.paperType().toLowerCase()] = true;
    } catch (e) {
        logToCentralServer("OpeOrderItem.prototype.resetUpsaleOptions() failed to reset", e, 3);
    }

    //Set imageEnhancement to true, but only if the product actually has this option
    var product = ProductStore.getProductById(this.getProductId());
    if (product) {
        if (product.hasUpsaleOption("imageEnhancement")) {
            this.options.upsale["imageEnhancement"] = true;
        }
    }
};

/**
 * Get the price of quantity 1 of this item
 * Choose if you want to exclude the prices for upsale options or not
 * @param  {int}
 * @param  {boolean}
 * @return {float}
 */
OpeOrderItem.prototype.getUnitPrice = function (decimals, excludingUpsaleOptions) {
    if (this.getProductId()) {
        if (!decimals) {
            decimals = 2;
        }

        var decimalMultiplier = Math.pow(10, decimals);

        //Get product
        var product = ProductStore.getProductById(this.getProductId());

        //Get width option price
        var price = product.getWidthOptionPrice(this.getProductWidth());

        if (!excludingUpsaleOptions) {
            //Get upsale options price
            for (var option in this.options.upsale) {
                if (this.options.upsale.hasOwnProperty(option)) {
                    if (this.options.upsale[option]) { //Option enabled
                        price += product.getOptionPriceByName(option);
                    }
                }
            }
        }

        //Round the number to include the number of decimals given as argument to this function
        return Math.round(price * decimalMultiplier) / decimalMultiplier;
    }

    return null;
};


/**
 * Set product id
 * @param {string}
 */
OpeOrderItem.prototype.setProductId = function (productId) {
    this.productId = productId;
};

/**
 * Set the product width option of this item
 * @param {string}
 */
OpeOrderItem.prototype.setProductWidth = function (width) {
    this.options.productWidth = width;
    // @TODO: Remove
    //var dpi = this.getDPI();
};

/**
 * Get the product width option for this item
 * @return {string}
 */
OpeOrderItem.prototype.getProductWidth = function () {
    return this.options.productWidth;
};

OpeOrderItem.prototype.setProductHeight = function (height) {
    this.options.productHeight = height;
};

OpeOrderItem.prototype.getProductHeight = function () {
    return this.options.productHeight;
};

/**
 * Return the aspect ratio of a product
 * @return {float}
 */
OpeOrderItem.prototype.getAspectRatio = function () {
    return this.options.productWidth / this.options.productHeight;
};

/**
 * Increase the quantity by 1
 */
OpeOrderItem.prototype.increaseQuantity = function () {
    this.options.quantity += 1;
};

/**
 * Set the quantity
 * @param {int}
 */
OpeOrderItem.prototype.setQuantity = function (quantity) {
    this.options.quantity = parseInt(quantity);
};

/**
 * Decrease quantity by one, but not below zero
 */
OpeOrderItem.prototype.decreaseQuantity = function () {
    if (this.options.quantity > 0) {
        this.options.quantity += 1;
    }
};

/**
 * Get the quantity
 * @return {int}
 */
OpeOrderItem.prototype.getQuantity = function () {
    return parseInt(this.options.quantity);
};

/**
 * Set image filter value.
 * Fex:
 * "rotate.angle", 90
 *
 *
 * This function can not be used to set effects on the filter, since effects is an array and we might want to add, remove or replace
 * @param {string}
 * @param {string}
 */
OpeOrderItem.prototype.setImageFilterValue = function (filterName, value) {
    var path = filterName.split('.');

    if (path[0] == "effect" || path[0] == "effects") {
        throw new Error("Cannot use setImageFilterValue to set effects, since effects is an array it needs more complex handling. There is seperate functions you can use, called replaceFilterEffect and addFilterEffect");
    }

    //The filtername is a string like this: "rotate.angle", and the object we store it in
    //does not have a rotate object within it, or any other object at first. So we need to create
    //the rotate object before we can assign it the angle key value pair
    switch (path.length) {
        case 1:
            this.options.imageFilter[path[0]] = value;
            break;
        case 2:
            if (!this.options.imageFilter[path[0]]) {
                this.options.imageFilter[path[0]] = {};
            }
            this.options.imageFilter[path[0]][path[1]] = value;
            break;
        case 3:
            if (!this.options.imageFilter[path[0]]) {
                this.options.imageFilter[path[0]] = {};
            }
            if (!this.options.imageFilter[path[0]][path[1]]) {
                this.options.imageFilter[path[0]][path[1]] = {};
            }
            this.options.imageFilter[path[0]][path[1]][path[2]] = value;
            break;

        default:
            throw new Error("setImageFilterValue only supports setting filter 3 levels deep. This could be changed quite easily if needed");
            break;
    }
};

/**
 * Since the image filter effects (sephia, black/white...) is an array of effects, but we usually
 * only allow one effect at a time, this function can replace the existing filter in the list
 * with a new one, instead of adding it.
 * @param  {Object} effect - usually looks like this: {type: "Sephia"}
 */
OpeOrderItem.prototype.replaceFilterEffect = function (effect) {

    //Remove all exisitng effects by creating a new array
    this.options.imageFilter.effects = [];

    //Add the effect
    this.options.imageFilter.effects.push(effect);
};

/**
 * Add a filter effect. The filter effect is an array so there can be multiple
 * effects. But usually we only do one at a time.
 * @param {Object} effect - usually looks like this: {type: "Sephia"}
 */
OpeOrderItem.prototype.addFilterEffect = function (effect) {

    //Add the effect
    this.options.imageFilter.effects.push(effect);
};

/**
 * Get a json object of all the applied image filters
 * @return {object}
 */
OpeOrderItem.prototype.getImageFilters = function (includeCrop) {
    return this.options.imageFilter;
};

/**
 * Get an object with the width and height based on the images rotation
 * @return {object} - with width and height
 */
OpeOrderItem.prototype.getSizeWithRotation = function () {
    var orderImage = ImageStore.getImageById(this.imageId);
    var imageWidth = orderImage.getWidth();
    var imageHeight = orderImage.getHeight();
    var imageSize = {width: imageWidth, height: imageHeight};

    try {
        var angle = this.options.imageFilter.rotate.angle;
    } catch (e) {
        var angle = 0;
    }
    if (angle == 90 || angle == 270 || angle == -90 || angle == -270) {
        return {width: imageHeight, height: imageWidth};
    }

    return imageSize;
};

/**
 * Get center positions in percentage
 *
 * @param {object} size
 *
 * @return {object} - with x and y percentage
 */
OpeOrderItem.prototype.getPercentage = function (size, isUpload) {

    var imageSize = (size ? size : this.getSizeWithRotation());
    var productHeight = this.getProductHeight();
    var productWidth = this.getProductWidth();
    
    // For polaroid products, use canvas dimensions instead of product dimensions
    var customSettings = this.getCustomSettings();
    if (customSettings && customSettings.canvasWidth && customSettings.canvasHeight) {
        productWidth = parseFloat(customSettings.canvasWidth);
        productHeight = parseFloat(customSettings.canvasHeight);
    }
    
    var percentage = {
        percentageX: 0,
        percentageY: 0
    };

    var imageHeight = imageSize.height;
    var imageWidth = imageSize.width;
    var _isPortrait = isUpload ? imageHeight > imageWidth : this.isPortrait;

    // Images can be portrait but rotated so it might happen that we get image height and width replaced
    // Hence we need to get correct ratio for images same as product ratio
    var imageRatio = _isPortrait ? imageHeight / imageWidth : imageWidth / imageHeight;
    var productRatio = productWidth / productHeight;

    var diff = (imageRatio > productRatio) ? (imageRatio - productRatio) : (productRatio - imageRatio);

    // if the ratio is very small, then we don't have to crop anything
    if (diff < Config.ProductRatioWarning && isUpload) {
        return percentage;
    }

    var cropY = _isPortrait;

    // on vertical images, when you rotate the with and height is replaced.
    // but we still have isPortrait property on this. Also, we have rotate angle
    // we will check that to be sure that is portrait has been rotated horizontally
    var _imageFilter = this.options.imageFilter;
    if (!isUpload && _isPortrait && _imageFilter && _imageFilter.rotate) {
        cropY = _imageFilter.rotate.angle === 180 || _imageFilter.rotate.angle === 0;
    } else if (!isUpload && !_isPortrait && _imageFilter.rotate) {
        cropY = _imageFilter.rotate.angle === 90 || _imageFilter.rotate.angle === 270;
    }

    // Use the same approach as editor-output-creator.js for consistency
    // Calculate the crop dimensions as percentages, then center by taking half the excess
    
    var imageRatio = _isPortrait ? imageHeight / imageWidth : imageWidth / imageHeight;
    var productRatio = productWidth / productHeight;
    
    var newWidth, newHeight;
    
    if (productRatio == 1) {
        // Square product
        newWidth = (imageWidth <= imageHeight ? imageWidth : imageHeight);
        newHeight = (imageWidth <= imageHeight ? imageWidth : imageHeight);
    } else if (productRatio < imageRatio && _isPortrait) {
        // Portrait image, smaller ratio, cut height to make it fit the product
        newWidth = imageWidth;
        newHeight = imageWidth * productRatio;
    } else if (productRatio < imageRatio && !_isPortrait) {
        // Landscape image, smaller ratio, cut width to make it fit the product
        newWidth = imageHeight * productRatio;
        newHeight = imageHeight;
    } else if (productRatio > imageRatio && _isPortrait) {
        // Portrait image, larger ratio, cut width to make it fit the product
        newWidth = imageHeight / productRatio;
        newHeight = imageHeight;
    } else if (productRatio > imageRatio && !_isPortrait) {
        // Landscape image, larger ratio, cut height to make it fit the product
        newWidth = imageWidth;
        newHeight = imageWidth / productRatio;
    } else {
        // No cropping needed
        newWidth = imageWidth;
        newHeight = imageHeight;
    }
    
    // Calculate crop percentages
    var cropWidthPercent = (newWidth / imageWidth) * 100;
    var cropHeightPercent = (newHeight / imageHeight) * 100;
    
    // Center the crop by taking half of the excess
    var FULL_IMAGE_PERCENTAGE = 100;
    var cropWidthOffset = (FULL_IMAGE_PERCENTAGE - cropWidthPercent) / 2;
    var cropHeightOffset = (FULL_IMAGE_PERCENTAGE - cropHeightPercent) / 2;
    
    // Convert to the coordinate system used by the UI (negative values for centering)
    if (cropWidthPercent < FULL_IMAGE_PERCENTAGE) {
        percentage.percentageX = -cropWidthOffset / 100;
    }
    if (cropHeightPercent < FULL_IMAGE_PERCENTAGE) {
        percentage.percentageY = -cropHeightOffset / 100;
    }

    return percentage;
};

/**
 * Set percentage
 *
 * @param {object} size
 */
OpeOrderItem.prototype.setPercentage = function (size, isUpload) {
    var percentage = this.getPercentage(size, isUpload);

    this.options.position.percentageX = percentage.percentageX;
    this.options.position.percentageY = percentage.percentageY;
};

/**
 * Save crop width and height. This is useful for the JSON output
 * when creating filters
 * @param  {float} width  percentage width
 * @param  {float} height percentage height
 */
OpeOrderItem.prototype.saveCropWidthAndHeight = function (width, height) {
    if (!this.options.hasOwnProperty("crop") || !this.options.crop) {
        this.options.crop = {};
    }

    this.options.crop.h = height;
    this.options.crop.w = width;
};

/**
 * Remove crop width and height.
 */
OpeOrderItem.prototype.removeCropWidthAndHeight = function () {
    delete this.options.crop;
};

/**
 * Method for getting the DPI of the order item
 * @param {float} - scale
 * @return {number} - The DPI of the image
 */
OpeOrderItem.prototype.getDPI = function (scale) {
    var productData = {productWidth: this.options.productWidth, productHeight: this.options.productHeight},
        image = ImageStore.getImageById(this.imageId),
        imageWidth, imageHeight, dpi;

    if (!scale) scale = this.options.scale;

    dpi = ProductCalculations.getDPI(productData, image.getWidth(), image.getHeight(), scale);

    return dpi;
};


/**
 * Method for getting customSettings for an order item
 * @return {object} - The customSettings object
 */
OpeOrderItem.prototype.getCustomSettings = function () {
    var item = this;
    var prodKey = (this.options.productHeight + 'x' + this.options.productWidth).toString();
    var prodOptions = ProductStore.getProductById(item.productId).options;
    if (prodOptions.customSettings) {
        if (prodOptions.customSettings[prodKey]) {
            return prodOptions.customSettings[prodKey];
        }
        else {
            return null;
        }
    }
};

OpeOrderItem.prototype.setLocalImageUploadStatus = function (status) {
    if (!this.hasOwnProperty("localImageUploadStatus")) {
        this.localImageUploadStatus = [];
    }
    this.localImageUploadStatus.push(status);
};


OpeOrderItem.prototype.getLocalImageUploadStatus = function () {
    return this.localImageUploadStatus;
};

/**
 * Create a random string
 */
OpeOrderItem.prototype._makeId = function (size) {
    var text = "";
    var possible = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";

    for (var i = 0; i < size; i++)
        text += possible.charAt(Math.floor(Math.random() * possible.length));

    return text;
};

export default OpeOrderItem;
